import { Injectable, computed, signal, TemplateRef, ViewContainerRef } from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';

/**
 * Service for managing page-level action buttons.
 *
 * Routed components can register action templates via PageActionsDirective,
 * which are then rendered in the layout's page header bar (aligned with breadcrumbs).
 *
 * Only one component's actions can be active at a time - the last registered wins.
 * Actions are automatically cleared when the registering component is destroyed.
 *
 * @example
 * ```typescript
 * // In a routed component
 * private readonly pageActionsService = inject(PageActionsService);
 *
 * ngAfterViewInit(): void {
 *   this.pageActionsService.attach(this.actionsTemplate, this.viewContainerRef);
 * }
 *
 * ngOnDestroy(): void {
 *   this.pageActionsService.detach();
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PageActionsService {
  private readonly _portal = signal<TemplatePortal | null>(null);

  /** The current portal to render (read-only signal) */
  readonly portal = this._portal.asReadonly();

  /** Whether a portal is currently attached (for conditionally rendering the actions bar) */
  readonly hasPortal = computed(() => this._portal() !== null);

  /**
   * Attach a template to be rendered in the page header actions slot.
   * Called by PageActionsDirective when a component mounts.
   *
   * @param template The template containing the action buttons
   * @param viewContainerRef The view container for creating the portal
   */
  attach(template: TemplateRef<void>, viewContainerRef: ViewContainerRef): void {
    const portal = new TemplatePortal(template, viewContainerRef);
    this._portal.set(portal);
  }

  /**
   * Detach the current template.
   * Called by PageActionsDirective when a component unmounts.
   */
  detach(): void {
    this._portal.set(null);
  }
}
